'use client';

import { useState } from 'react';
import { Search, FileText, Clock, Building, User, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { useLanguage } from '@/lib/language-context';

export function TrackComplaintClient() {
  const [mobileNo, setMobileNo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [complaints, setComplaints] = useState<any[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const { t } = useLanguage();

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mobileNo) return;

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/complaints?mobile_no=${mobileNo}`);
      const data = await res.json();

      if (!res.ok) throw new Error(data.error || 'Failed to fetch complaints');

      setComplaints(data.complaints);
      if (data.complaints.length === 0) {
        setError(t('noComplaints'));
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'resolved': return 'text-green-600 bg-green-50 border-green-200';
      case 'rejected': return 'text-red-600 bg-red-50 border-red-200';
      case 'in progress': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-orange-600 bg-orange-50 border-orange-200';
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Search Section */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="px-8 py-6 text-white text-center" style={{ background: 'linear-gradient(135deg, #4A2E14, #E67E22)' }}>
          <h2 className="text-2xl font-bold mb-2">{t('trackGrievance')}</h2>
          <p className="text-white/80">{t('trackSubtitle')}</p>
        </div>
        <div className="p-8 text-center">
          <form onSubmit={handleSearch} className="max-w-md mx-auto relative flex items-center">
            <input
              type="text"
              required
              pattern="[0-9]{10}"
              placeholder={t('enterMobile')}
              value={mobileNo}
              onChange={(e) => setMobileNo(e.target.value)}
              className="w-full pl-6 pr-32 py-4 rounded-full border-2 border-gray-200 focus:border-[#E67E22] outline-none transition-all text-lg"
            />
            <button
              type="submit"
              disabled={loading}
              className="absolute right-2 top-2 bottom-2 text-white px-6 rounded-full font-bold transition-colors disabled:opacity-70 flex items-center gap-2"
              style={{ background: 'linear-gradient(135deg, #F39C12, #E67E22)' }}
            >
              {loading ? <Clock className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              {t('search')}
            </button>
          </form>
          {error && (
            <p className="text-red-500 mt-4 font-medium">{error}</p>
          )}
        </div>
      </div>

      {/* Results Section */}
      {complaints.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-gray-900 px-2 flex items-center gap-2">
            <FileText className="w-6 h-6 text-[#D4A017]" />
            {t('yourComplaints')} ({complaints.length})
          </h3>

          {complaints.map((complaint) => (
            <div key={complaint.id} className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden transition-all hover:shadow-lg">
              <div
                className="p-6 cursor-pointer flex flex-col md:flex-row md:items-center justify-between gap-4"
                onClick={() => toggleExpand(complaint.id)}
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-3">
                    <span className="font-mono font-bold" style={{ color: '#E67E22' }}>{complaint.complaint_no}</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getStatusColor(complaint.status)}`}>
                      {complaint.status}
                    </span>
                  </div>
                  <h4 className="font-bold text-lg text-gray-900">{complaint.title}</h4>
                  <p className="text-sm text-gray-500 flex items-center gap-2">
                    <Clock className="w-4 h-4" /> {new Date(complaint.created_at).toLocaleString()}
                  </p>
                </div>

                <div className="flex items-center gap-6">
                  <div className="text-sm">
                    <p className="text-gray-500 mb-1 flex items-center gap-1"><Building className="w-4 h-4" /> {t('department')}</p>
                    <p className="font-semibold">{complaint.department?.dept_name || t('notAssigned')}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-400">
                    {expandedId === complaint.id ? <ChevronUp className="w-6 h-6" /> : <ChevronDown className="w-6 h-6" />}
                  </div>
                </div>
              </div>

              {/* Expanded Details & Timeline */}
              {expandedId === complaint.id && (
                <div className="border-t border-gray-100 bg-gray-50 p-6 md:p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

                    {/* Details */}
                    <div>
                      <h5 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-gray-400" />
                        {t('complaintDetails')}
                      </h5>
                      <div className="bg-white p-5 rounded-xl border border-gray-100 space-y-4">
                        <div>
                          <p className="text-xs text-gray-500 uppercase font-semibold mb-1">{t('description')}</p>
                          <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{complaint.description}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-50">
                          <div>
                            <p className="text-xs text-gray-500 uppercase font-semibold mb-1">{t('priorityLabel')}</p>
                            <p className="text-sm font-medium">{complaint.priority}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase font-semibold mb-1">{t('address')}</p>
                            <p className="text-sm">{complaint.district}, {complaint.taluk}</p>
                          </div>
                        </div>
                        {complaint.attachments && complaint.attachments.length > 0 && (
                          <div className="pt-4 border-t border-gray-50">
                            <p className="text-xs text-gray-500 uppercase font-semibold mb-2">{t('attachments')}</p>
                            <div className="flex flex-wrap gap-2">
                              {complaint.attachments.map((att: any) => (
                                <a
                                  key={att.id}
                                  href={att.file_path}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-xs bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg border border-blue-100 hover:bg-blue-100"
                                >
                                  {att.file_name || t('viewFile')}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Timeline */}
                    <div>
                      <h5 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-gray-400" />
                        {t('statusTimeline')}
                      </h5>
                      <div className="relative border-l-2 border-gray-200 ml-4 space-y-6 pb-4">
                        {complaint.timeline?.map((t_item: any) => (
                          <div key={t_item.id} className="relative pl-6">
                            <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-white border-2" style={{ borderColor: '#E67E22' }}></div>
                            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm relative -top-1.5">
                              <div className="flex justify-between items-start mb-2">
                                <span className="font-bold text-sm" style={{ color: '#E67E22' }}>{t_item.action}</span>
                                <span className="text-xs text-gray-400 font-medium whitespace-nowrap ml-2">
                                  {new Date(t_item.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                                </span>
                              </div>
                              {t_item.remarks && <p className="text-sm text-gray-600 mb-2">{t_item.remarks}</p>}
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <User className="w-3 h-3" /> {t_item.officer_name}
                                {t_item.department && <><span className="mx-1">•</span> <Building className="w-3 h-3" /> {t_item.department}</>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
